/**
 * 组标题下那句「这一组要选几样」——**三处 getDesc 共用的唯一实现**。
 * The one implementation of a section's "how many to pick" caption.
 *
 * 为什么必须共用一处：这句话里的数字，和「换选 / 置灰 / 数量步进 / 单选渲染 /
 * 预勾选截断」问的是同一个问题（有效上限，见 ./sectionLimit）。本仓三份 getDesc
 * 的 `exactly` 分支原本各写了一遍这个三元式：
 *
 *   tParams('select_n', 'Select {n}', { n: item.maxOption ? item.maxOption : item.minOption })
 *
 * 而 **JS 里字符串 `'0'` 是真值**，于是 `maxOption: '0'` 的「恰好选 1 样」组
 * （上限写在 minOption 上、max 存成 '0' 是后端真实的数据形状，见 sectionLimit.js）
 * 在屏上念成 `Select 0`，而同一个组的截断 / 置灰 / 换选 / 单选渲染 / 数量步进
 * 全部按有效上限算出的 **1** 在跑 —— 页面自己跟自己打架，顾客照着「Select 0」
 * 根本没法理解为什么还必须选一个。
 *
 * 修法不是在每一处各补一个更聪明的三元式：那正是分叉的来源（本仓已经因此分叉过，
 * 三处写法一样但都和有效上限对不上）。数字只有一个来源 —— `sectionMaxOption`。
 *
 * The caption's number must come from the same resolver every other "how many can
 * this section take" decision uses; hand-rolled fallbacks at each call site are
 * exactly how the copies drifted apart in the first place.
 */

import { tParams } from '@/utils/language'
import { NO_CAP, sectionBounds, sectionLimitKind, sectionMaxOption } from './sectionLimit'

/**
 * 「至少选 N 样」。
 *
 * @param {Number} n 念出来的那个数
 * @param {Boolean} withRequiredSuffix 句尾要不要 `(Required)`
 * @returns {String}
 */
export const atLeastCopy = (n, withRequiredSuffix) => (withRequiredSuffix
  ? tParams('select_at_least_n_required', 'Select at least {n} (Required)', { n })
  : tParams('select_at_least_n', 'Select at least {n}', { n }))

/**
 * `limit` 组没选够时的那句红字 —— **11 处调用点共用的唯一实现**。
 *
 * 那一支的校验条件就是 `parseInt(selectedNum) < 1`（至少选一样），所以念的是
 * 「至少选 1 样（必选）」。以前 11 处各念一句
 * `tParams('select_up_to_n_required', …, { n: minOption })` ——「**最多**选择
 * {minOption} 项（必选）」，而 `limit` 组的 `minOption` 通常是 0 或空，屏上就成了
 * 「最多选择 0 项（必选）」/ `Select up to  (Required)`：句子本身是反的，数字还是假的。
 *
 * 那个 `1` 只写在这一处：以后要改「至少几样」改这里，不用去 11 个调用点找。
 *
 * @returns {String}
 */
export const limitWrongCopy = () => atLeastCopy(1, true)

/**
 * 套餐子品那行「还要选几样」—— 主商品页与多规格弹层共用的唯一实现。
 *
 * 调用方是 `verificationComboRequired`：它把这个子品**每个必选配菜组的下限加起来**
 * （`sectionLowerBound`，必选组恒 >= 1），得到「这个子品至少还要选几样」。
 * 后端看不见套餐子品的配菜选择，所以这行字是这条路上唯一的防线 —— 但它的**计数**
 * 不归本函数管，本函数只负责把那个数说成人话。
 *
 * 2026-08-10 之前这里是**写死的英文模板串** `` `${num} selections required!` ``：
 *
 *   - **语言层**：一个字都不过语言包。法语 / 中文门店的顾客在满屏法语中文里
 *     看到一句英文 —— 而同一个屏幕上别的红字（`limitWrongCopy` /
 *     `rangeWrongCopy`）早就是本地化的，所以是同一行字自己跟旁边打架。
 *   - **措辞层**：`3 selections required!` 是**报错口气**（名词化的 selections、
 *     被动的 required、还带感叹号），它告诉顾客「你错了」，不告诉顾客**该干什么**。
 *     项目规则要的是「动词 + 结果」：`Select at least 3 (Required)` /
 *     `至少选择 3 项（必选）` —— 一眼看得出下一步是去选东西。
 *
 * **为什么是「至少 N 样」而不是「正好 N 样」**：那个数是各组**下限**之和。
 * 组里有 `range 2–5` 这种的时候，顾客选到 5 样也是对的 —— 说「正好 2 样」会
 * 宣布一条界面根本不执行的规则（与 `rangeCopy` 头注释里那条「只念真的会执行的
 * 那一半」是同一条规则）。「至少 N 样」在四种 limitType 上都是真话。
 *
 * **为什么带 `(Required)` 后缀**：这行字长在 `requiredComboList` 的子品行上，
 * 而 `num` 只数 `type === 'required'` 的组 —— 两头都是必选，后缀是实话。
 * 它也让这句与屏上另外两句红字（`limitWrongCopy` / `rangeWrongCopy`）用同一套词，
 * 顾客不用在同一个页面上学两套说法。
 *
 * ⚠️ **不新增 i18n key**：`select_at_least_n_required` 是既有键，en / fr / zh 三份
 * 内建文案都在，管理端翻译表里也已经有它（`limitWrongCopy` 一直在用同一句）。
 * 本仓有 27 个键在管理端翻译表里是缺的，再添一个只会让缺口变大。
 *
 * @param {Number} n 各必选组下限之和 —— 这个子品至少还要选几样
 * @returns {String}
 */
export const comboRequiredCopy = (n) => atLeastCopy(n, true)

/**
 * `range` 组那句「要选几样」——**全仓唯一实现**：组头那句，加上 11 处红字提示
 * （8 处 `select_between_n_m_required` + 3 处写死英文的模板串），共用这一份。
 *
 * 上一版这句话在那 12 个地方各写一遍 `{ n: item.minOption, m: item.maxOption }`，
 * 两个数都是**后台原值直出**，于是顾客会看到这些字：
 *
 *   - `maxOption: '0'`（后端真实形状）→ **`Select between 2 and 0`**
 *   - `maxOption: ''`               → **`Select between 2 and `**
 *   - `minOption: '2.5'`            → **`Select between 2.5 and 3`**，而校验卡的是
 *     `parseInt('2.5')` = **2**
 *
 * 同一个组的置灰 / 步进 / 预勾选截断全都按有效上限（`sectionMaxOption`，没写就是
 * 不封顶）在跑 —— 页面自己跟自己打架，而顾客只能看见那句假规则。
 *
 * 决策表：
 *
 *   上限 = sectionMaxOption（跨仓上限契约）
 *   下限 = `requiredLowerBound`（= max(1, 后台配的下限)，见 ./sectionLimit）
 *          —— 就是那六道闸门读的那个数（闸门那边叫 `sectionLowerBound`，同一个地板）
 *
 *   **可选组**（含没写 `type` 的组）—— 下限一个字都不宣布：
 *     上限 ✓  →  Select up to {上限}
 *     上限 ✗  →  ''（一个字都不念）
 *
 *   **必选组** —— 地板 1，后台没配下限也一样（见下方「为什么是 1」）：
 *     上限 ✓ 且 上限 >= 下限  →  Select between {下限} and {上限}
 *     否则                    →  Select at least {下限}
 *
 * ── 可选组为什么不宣布下限（owner 2026-08-10 裁定）────────────────────────
 *
 * 裁定：**可选组的下限「不执行」。**
 *
 * 本仓的事实与裁定一致：三条校验路（`getCheckNum` / `validateAndUpdateMod` /
 * `_validateRequiredModifications`）全都长在 `type === 'required'` 门里，
 * 六道下限闸门一道都不看可选组。所以 `optional range min=2 max=5` 的**真实行为**
 * 是「最多 5 样，一样不选也能过」。以前这里念的是「Select between 2 and 5」/
 * 「Select at least 2」—— **屏幕说一套、闸门做另一套**，而顾客只看得见屏幕那一套。
 *
 * 只讲上限不是「少说了点什么」：上限那一半**真的会执行** —— 选到上限其余选项立刻
 * 置灰（三处 `getCheckNum` 的置灰分支 + utils/modSectionState 的
 * `applySectionSelectionLimits` / `applySingleSelectExclusive`）。
 * 顺带修好的还有「上限 < 下限」那种配反了的可选组：以前念「至少 3 样」（谁都不执行），
 * 现在念「最多 1 样」—— 那正是置灰真正在做的事。
 *
 * ⚠️ 判据是「**是不是必选**」，不是 `type === 'optional'`：没写 `type` 的组同样
 * 不做校验，跟可选组走同一边（与 ./sectionLimit 的 `isRequiredSection` 同源）。
 *
 * **区间反了（上限 < 下限）的必选组为什么退成「至少 N」**：校验实际拦的是
 * `selectedNum < minOption`，真正生效的就是下限；此时上限那一侧的
 * `selectedTotal > sectionLimit` 只会让这组无论怎么点都过不了，念一句
 * 「Select between 3 and 1」等于把顾客往一个走不通的操作上指。念下限才诚实。
 *
 * **必选组下限念不出时为什么是 1**：因为闸门就是这么拦的 —— 必选组的地板是 1
 * （owner 2026-08-10：「必选组不存在连 1 个都不选就能过去的」，契约见
 * ./sectionLimit 的 `requiredLowerBound` / `sectionLowerBound`）。
 * 这句话与 `limit` 支已经定下的「Select at least 1 (Required)」是同一句话、
 * 同一个理由。
 *
 * ⚠️ **这段话在 2026-08-10 之前是不成立的，别照抄旧版本的理由。** 上一版写的是
 * 「实际执行的是『这一组必须被填过一次』，随便点一下就会进 `passList`」——
 * 那不是「至少选 1 样」：勾一下**再取消**同样进 `passList`，于是这组带着
 * **0 个选择**加进了购物车，而屏上一直念着「Select between 1 and 3 (Required)」。
 * 屏幕说一套、闸门做另一套 —— 现在是闸门跟上了屏幕，不是屏幕迁就闸门。
 *
 * 地板同时保证**必选组永远念得出话**（永远是真值），于是
 * `_validateRequiredModifications` 的 `filter(item => item.wrong && …)` 这一句
 * 不会因为空串而漏放。必选组这一支只有两条出路（完整区间 / 至少 N）：
 * `Select up to N` 与「不念」它一条都走不到，所以这两条**长在可选组那一支里**，
 * 不再是共用的兜底尾巴。
 *
 * ⚠️ **两个开关是正交的，别再合成一个布尔**（2026-08-09 第二次收口修的就是这个）：
 * 合成一个的那一版里，组头传 `false`、红字传 `true`，于是「必选 + `min` 空 +
 * `max: '3'`」这个**很常见**的形状（店家只填了上限那一栏）会让同一个组
 * 自己跟自己打架：
 *
 *   组头（拿不到兜底）→ 走上限支 →「Select up to 3」（言下之意 0 样也行）
 *   红字（拿得到兜底）→「Select between 1 and 3 (Required)」
 *
 * 「这一组是不是必选」决定**下限念不念、念不出时兜不兜底 1**，
 * 「句尾拼不拼 `(Required)`」是排版 —— 组头那句的后缀由调用方 `getDesc` 另外拼，
 * 所以它必须 `isRequiredGroup: true` 但 `withRequiredSuffix: false`，否则拼两遍。
 *
 * @param {Object} mod 配菜组 / modification section
 * @param {Object} [opts]
 * @param {Boolean} [opts.isRequiredGroup] 这一组是不是必选 —— 决定下限念不念、以及念不出时兜底 1
 * @param {Boolean} [opts.withRequiredSuffix] 句尾要不要 `(Required)`
 * @returns {String} 文案；可选组念不出上限时是空串（必选组永远念得出）
 */
export const rangeCopy = (mod, { isRequiredGroup, withRequiredSuffix } = {}) => {
  // ⚠️ **两个数必须出自同一次 `sectionBounds` 计算。**「这一组是不是必选」由调用方
  // 显式传（`isRequiredGroup`），而它同时决定地板与兜底的走向 —— 分两次算
  // （上限问 `sectionMaxOption`、下限问 `requiredLowerBound`）就等于让两头
  // 按不同的组类型解析，`min > max` 这种坏配置上会当场对不上。
  const { lower: minN, upper: maxN } = sectionBounds(mod, !!isRequiredGroup)

  // 可选组（含没写 type 的组）：下限不执行，所以一个字都不宣布它 —— 只讲上限。
  // 上限那一半是真的会执行的（选到上限其余选项置灰），念不出上限就一个字都不念。
  if (!isRequiredGroup) {
    if (maxN === NO_CAP) return ''
    // ⚠️ **这一支的 `withRequiredSuffix: true` 在生产上走不到**（2026-08-10 查证）：
    // 两个调用方一个恒 `isRequiredGroup: true`（`rangeWrongCopy`），另一个恒
    // `withRequiredSuffix: false`（`sectionPickDesc`），组合起来「可选组 + 拼(必选)」
    // 无人产出 —— 它本来就是**自相矛盾**的一对参数（可选组不是必选组）。
    //
    // **仍然保留**，理由不是「以后可能用」（那是 YAGNI 该删的东西），是这两条：
    //   1. 删掉它，这个组合会**静默**掉后缀 —— 把一个惰性死分支换成一个不响的文案坑。
    //   2. `select_up_to_n_required` 这个键**五仓共用**（kiosk 的 defaults.ts、
    //      管理端翻译表里都有），单在本仓删掉只会让各仓的键集合分叉。
    // 不可达这件事由测试钉着（productDetailComboRequiredCopy.spec.js），
    // 哪天有调用方真的传出这个组合，那条测试会先红、逼人回来读这段。
    return withRequiredSuffix
      ? tParams('select_up_to_n_required', 'Select up to {n} (Required)', { n: maxN })
      : tParams('select_up_to_n', 'Select up to {n}', { n: maxN })
  }

  // 两头是同一个数 → 念「正好选 N 样」，不念「Select between 2 and 2」。
  // 这一格现在有两种来路，都得念同一句：后台就写了 `min = max = 2`；
  // 或者后台把区间配反了（`min=5 / max=2`），§8 的兜底把它塌成了「正好 2 样」。
  // 后者以前念的是「Select at least 5」——**一句谁都执行不了的话**：选到 2 样
  // 其余选项就全灰了，顾客照着屏幕怎么点都到不了 5。
  if (maxN !== NO_CAP && maxN === minN) {
    return withRequiredSuffix
      ? tParams('select_n_required', 'Select {n} (Required)', { n: maxN })
      : tParams('select_n', 'Select {n}', { n: maxN })
  }
  if (maxN !== NO_CAP && maxN > minN) {
    return withRequiredSuffix
      ? tParams('select_between_n_m_required', 'Select between {n} and {m} (Required)', { n: minN, m: maxN })
      : tParams('select_between_n_m', 'Select between {n} and {m}', { n: minN, m: maxN })
  }
  // 走到这里只剩一种情况：**上限缺失**（`maxN === NO_CAP`），生效的只有下限。
  // ⚠️ 「区间反了」**再也走不到这里** —— §8 的兜底在 `sectionBounds` 里就把它
  // 塌平了（两头都有数且下限更大 → 一律塌成同一个 N），所以 `maxN < minN`
  // 在这一行不可达。别再往这句上挂「区间反了」的解释，那是上一版的事实。
  return atLeastCopy(minN, withRequiredSuffix)
}

/**
 * `range` 组没选够（或选超了）时的那句红字 —— **11 处调用点共用的唯一实现**。
 *
 * 红字那三条路本来就只在 `type === 'required'` 的门里跑，所以两个开关永远是这一对；
 * 把它们抄在 11 个调用点上，与把 `1` 抄在 11 处是同一个毛病（散出去的东西从一个数
 * 变成一对开关而已）。调用点只写 `rangeWrongCopy(item)`，与 `limitWrongCopy()` 对称。
 *
 * 组头那句**不**走这里：它要如实传 `isRequiredGroup`、并且必须关掉后缀
 * （`getDesc` 自己拼 `(Required)`），见 `sectionPickDesc`。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {String}
 */
export const rangeWrongCopy = (mod) => rangeCopy(mod, { isRequiredGroup: true, withRequiredSuffix: true })

/**
 * 「这一组要选几样」这句文案（不含末尾的 `(Required)`，那一段由调用方拼）。
 *
 * 没有生效上限（`NO_CAP`）时**一个字都不念**，这是本函数定死的语义：
 *
 *  - 念不出数字的时候，唯一诚实的选项是不念。`Select undefined` / `Select ` /
 *    `Select up to 2.5` 都是在向顾客宣布一条界面根本不会执行的规则 —— 上限解析
 *    不出来的组，点击侧、置灰侧、步进侧全都按「不封顶」处理，这时候标题再说
 *    「最多 2.5 样」就是页面自己跟自己打架。
 *  - 不念的代价只是少一行灰色小字，组名和选项都还在，顾客照样能点；念错的代价是
 *    顾客照着一个假规则去理解整组，然后发现点不通。
 *  - 必选组的 `(Required)` 由调用方另外拼，所以「不念」不会把必选这件事一起吞掉。
 *
 * 这条门 `limit` / `range` / `exactly` 三种都会撞上。`exactly` 那一支要看组类型
 * （owner 2026-08-08 第四次裁定，见 ./sectionLimit）：
 *
 *  - **必选**的 `exactly` 组永远给得出一个正整数（说不出就是 1），所以它一定念得出
 *    那句 `Select N` —— 而且念的就是校验侧比对的那个数。
 *  - **可选**的 `exactly` 组两个键都没写出数时是 NO_CAP，于是这里**一个字都不念**。
 *    这不是退化：那种组不做校验、也不封顶，顾客可选任意个，屏上再念一句
 *    「Select 1」就是在宣布一条界面根本不会执行的规则。组名和选项都还在，照样能点。
 *  - **可选**的 `exactly` 组**说得出数**时念「最多 N 样」（`select_up_to_n`），
 *    不念「正好 N 样」（owner 2026-08-10 裁定 §9.2）。`exactly` 的那个数同时是
 *    上限和下限，而本仓只执行上限那一半 —— 勾 1 样照样过。这与可选 `range` 组
 *    「只讲上限」是同一条规则：判据是「下限有没有人执行」，不是 `limitType`。
 *
 * 门本身管的是「念不出就不念」这条语义，而不是某一个 limitType。
 *
 * `range` 的门店自定义文案（`modificationDescSettings.rangeDesc`）优先级最高，
 * 且在上限判断之前 —— 那句话是门店自己写死的整句，不带我们算出来的数字，
 * 上限坏不坏都不影响它该显示。
 *
 * `range` 那一支整个交给 `rangeCopy`（见上），所以它**不走**这道 NO_CAP 门 ——
 * 「必选 + 下限念得出、上限没写」的组要念的是 `Select at least 2`，不是一个字都不念。
 * 2026-08-09 那一轮同时把 11 处红字提示改成调同一个 `rangeCopy`，本函数与那 11 处
 * 从此是同一句话的同一份实现（之前是 12 份各写各的原值直出：11 处红字 + 组头这一句）。
 *
 * 必选组的下限那个数走的是 `requiredLowerBound`（= max(1, 后台配的下限)，见
 * ./sectionLimit）—— 六道闸门读的是同一个地板（那边包了一层 `sectionLowerBound`
 * 来分必选 / 可选），所以「屏上念的数」与「真正拦住顾客的数」在结构上就是同一个。
 * 注意组头这一支按组类型如实传 `isRequiredGroup`（`mod?.type === 'required'`），
 * 它同时管两件事（见 `rangeCopy` 头注释）：
 *
 *  - **必选组的组头照样吃兜底 1** —— 否则「必选 + min 空 + max 有值」会让组头念
 *    「Select up to 3」、红字念「Select between 1 and 3 (Required)」，
 *    同一个组说两句不一样的话（2026-08-09 第二次收口修的就是这个）。
 *  - **可选组的组头一个下限字都不念**（owner 2026-08-10 裁定：可选组的下限不执行）。
 *    可选组根本不会出现红字（三条校验路都在 `type === 'required'` 门里），
 *    所以这不是「两句话打架」，是「只有一句话，而它只讲真的会执行的那一半」。
 *
 * 组头与红字的差别只有句尾那个 `(Required)`：它由 `getDesc` 另外拼，
 * 所以这里必须 `withRequiredSuffix: false`，否则拼两遍。
 *
 * 已经不在这份名单里的是 `select_n_required`（8 处）：`exactly` 组的 `minOption`
 * 不是下限，它**就是**有效上限，所以 2026-08-08 那一轮把这 8 处一并改成读
 * `sectionMaxOption(item)` —— 否则两个键都没写出数的组会念成 `Select  (Required)`，
 * 而同一个组的组头念的是兜底出来的 `Select 1`。
 * 那 8 处全都长在 `type === 'required'` 的门里，所以拿到的**一定**是个正整数，
 * 不会因为可选组的 NO_CAP 念成 `Select null (Required)`。
 *
 * @param {Object} mod 配菜组 / modification section
 * @param {String} [rangeDesc] 门店给 range 组配的整句自定义文案
 * @returns {String} 文案；没有可念的数字时是空串
 */
export const sectionPickDesc = (mod, rangeDesc) => {
  // `limitType` 归一化成三选一，**认不出来的一律当 `exactly`**（owner 2026-08-10）。
  // 以前这里比的是裸 `limitType`，末尾那句 `return ''` 于是同时管着两件不相干的事：
  // 「`limit` 组没配上限」与「压根没写 `limitType`」。后者现在念得出话了 ——
  // 它的闸门也真的会拦人（见 utils/sectionLimit 的 `sectionLimitKind`）。
  const limitKind = sectionLimitKind(mod)
  // 「这一组的下限有没有人执行」—— 本仓的答案就是「它是不是必选组」：三条校验路
  // （`getCheckNum` / `validateAndUpdateMod` / `_validateRequiredModifications`）
  // 全都长在 `type === 'required'` 门里。`range` 支与 `exactly` 支问的是**同一个**
  // 判据，所以只算一次：分两处写就是让它们哪天漂开（与 ./sectionLimit 的
  // `isRequiredSection` 同源，没写 `type` 的组跟可选组走同一边）。
  const isRequiredGroup = mod?.type === 'required'

  // 门店自定义整句优先：它不带我们算的数字
  if (limitKind === 'range' && rangeDesc) return rangeDesc
  // `range` 的两个数各自过一遍「念得出吗」，唯一实现在 rangeCopy —— 它自己管
  // 「念不出就不念」，所以这一支要在下面那道 NO_CAP 门之前。
  // `isRequiredGroup` 必须如实传，它同时管两件事（见 rangeCopy 头注释）：
  // 写死 false 会让必选组的组头与红字在「min 空、max 有值」上说两句不一样的话；
  // 写死 true 会让可选组宣布一条本仓根本不执行的下限（owner 2026-08-10 裁定）。
  // `withRequiredSuffix` 必须关掉：组头的 `(Required)` 由 getDesc 另外拼。
  if (limitKind === 'range') {
    return rangeCopy(mod, { isRequiredGroup, withRequiredSuffix: false })
  }

  const max = sectionMaxOption(mod)
  // 没有生效上限 = 没有可念的数字
  if (max === NO_CAP) return ''

  if (limitKind === 'limit') return tParams('select_up_to_n', 'Select up to {n}', { n: max })

  // `exactly`，以及任何认不出来的 `limitType`。
  //
  // ⚠️ **可选组念的是「最多 N 样」，不是「正好 N 样」**（owner 2026-08-10 裁定 §9.2）。
  // `exactly` 的那个数**同时是上限和下限**，而本仓只执行上限那一半：可选组一条
  // 校验路都不跑（三条全在 `type === 'required'` 门里），勾 1 样照样加进购物车；
  // 真正会发生的只有「选到 N 样其余选项置灰」。念「Select 3」是在宣布一条
  // 一处都不拦的规则 —— 和上一批收掉的「可选 range 组念『至少 2 样』」是**同一条
  // 规则的另一面**：那一批只收了 `range` 的下限，漏了 `exactly` 把下限藏在
  // 「正好」这两个字里。判据是「下限有没有人执行」，不是 `limitType`。
  //
  // 必选组不变：它的闸门就是 `selectedNum !== sectionMaxOption`（`=== N`），
  // 屏上念的和闸门比的是同一个数。
  if (!isRequiredGroup) return tParams('select_up_to_n', 'Select up to {n}', { n: max })
  return tParams('select_n', 'Select {n}', { n: max })
}
